import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import Container from '../layout/Container';

const ProtectedRoute = ({ children, requireAuth = true }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // For routes that don't require auth (like login/signup), render immediately
  // Don't block on auth check - let the auth check happen in background
  if (!requireAuth) {
    // If user is already authenticated, redirect them away from auth pages
    if (isAuthenticated) {
      // Preserve pending invite token even when auth routes immediately redirect.
      const inviteId = new URLSearchParams(location.search || '').get('invite');
      if (inviteId) {
        try {
          sessionStorage.setItem('pendingCompanyInviteId', inviteId);
        } catch {
          // ignore storage failures
        }
      }

      // Check if this is a new signup (user just authenticated from an auth page)
      // New signups should go to community library
      const isNewSignup = location.pathname.startsWith('/auth/register') ||
                         location.pathname.startsWith('/auth/verify-email') ||
                         location.pathname.startsWith('/auth/social-signup');
      const fromLocation = location.state?.from;
      const returnTo = fromLocation
        ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
        : null;

      // New signups land in the library shell. The welcome wizard decides whether
      // to open Assistant, Community, or an integration path after the user chooses.
      if (isNewSignup && !returnTo) {
        return <Navigate to="/library" replace />;
      }

      // Otherwise, use the return URL or default to library
      return <Navigate to={returnTo || '/library'} replace />;
    }
    // Otherwise, render the auth page immediately (don't wait for auth check)
    return children;
  }

  // For protected routes, show app shell immediately with loading in content area (quick load)
  if (isLoading) {
    return (
      <Container>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100%',
            width: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Render the protected component
  return children;
};

export default ProtectedRoute;
